/**
 * React Node View für den AnnotationLink-Node.
 *
 * Lese-Modus: Schlanker Inline-Chip (Dot + Label), klickbar.
 * Edit-Modus: Voller Chip mit Drag-Handle, Vorlauftext, Type-Badge, Edit/Delete/Popup-Toggle.
 */

import React, { useState, useCallback } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { NodeViewProps } from '@tiptap/core'
import { useAnnotationStore } from '../../store/annotations'
import { ANNOTATION_TYPE_COLORS, ANNOTATION_TYPE_LABELS } from '../../types/annotations'
import { EMBED_DRAG_DATA } from './EmbedDragPlugin'

export function AnnotationLinkView({ node, extension, editor, getPos, updateAttributes }: NodeViewProps) {
  const { annotationId, label } = node.attrs as { annotationId: string; label: string }

  const annotation       = useAnnotationStore(s => s.getAnnotation(annotationId))
  const updateAnnotation = useAnnotationStore(s => s.updateAnnotation)
  const color            = annotation ? ANNOTATION_TYPE_COLORS[annotation.type] : 'hsl(240 5% 56%)'

  const displayLabel = label || annotation?.title || 'Annotation'
  const showPopup    = annotation?.showPopup !== false
  const isEditable   = editor?.isEditable ?? false
  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editLabel, setEditLabel] = useState(label)

  const typeLabel = annotation ? ANNOTATION_TYPE_LABELS[annotation.type] : null
  const vorlauftext = annotation?.tooltip || ''

  function handleClick() {
    if (isEditing) return
    const cb = extension.options.onAnnotationClick
    if (cb) cb(annotationId)
  }

  function handleDragStart(e: React.DragEvent) {
    const pos = getPos()
    if (typeof pos !== 'number') return
    e.dataTransfer.setData(EMBED_DRAG_DATA, JSON.stringify({ pos }))
    e.dataTransfer.effectAllowed = 'move'
    const chip = (e.target as HTMLElement).closest('.ann-embed-chip')
    if (chip) e.dataTransfer.setDragImage(chip as HTMLElement, 10, 10)
  }

  const handleDelete = useCallback(() => {
    const pos = getPos()
    if (typeof pos !== 'number') return
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run()
  }, [editor, getPos, node.nodeSize])

  const handleEditSave = useCallback(() => {
    updateAttributes({ label: editLabel })
    setIsEditing(false)
  }, [editLabel, updateAttributes])

  function toggleShowPopup(e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    if (!annotation) return
    updateAnnotation(annotation.id, { showPopup: !showPopup })
  }

  // ── Lese-Modus: schlanker Inline-Chip ─────────────────────────────────────
  if (!isEditable) {
    return (
      <NodeViewWrapper as="span" className="ann-link-wrapper">
        <span
          className="ann-embed-chip ann-embed-chip--read"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-annotation-id={annotationId}
          data-type="annotation-link"
          style={{
            background: isHovered ? `${color}18` : 'transparent',
            color,
            borderBottomColor: `${color}44`,
          }}
          title={vorlauftext || `Zur Annotation: ${displayLabel}`}
        >
          <span className="ann-embed-chip__dot" style={{ background: color }} />
          {displayLabel}
          {typeLabel && (
            <span className="ann-embed-chip__badge" style={{ background: `${color}20`, color }}>{typeLabel}</span>
          )}
          {/* Popup-Status-Indikator wenn deaktiviert */}
          {!showPopup && (
            <span className="ann-embed-chip__indicator">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </span>
          )}
        </span>
      </NodeViewWrapper>
    )
  }

  // ── Edit-Modus: voller Chip mit allen Funktionen ──────────────────────────

  const tooltipParts: string[] = []
  if (typeLabel) tooltipParts.push(`Typ: ${typeLabel}`)
  if (vorlauftext) tooltipParts.push(vorlauftext)
  if (annotation?.tags?.length) tooltipParts.push(`Tags: ${annotation.tags.join(', ')}`)
  if (annotation?.position) tooltipParts.push(`Pos: ${annotation.position.x.toFixed(1)}, ${annotation.position.y.toFixed(1)}, ${annotation.position.z.toFixed(1)}`)
  if (annotation?.cloudId) tooltipParts.push(`Wolke: ${annotation.cloudId}`)
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join('\n') : `Zur Annotation: ${displayLabel}`

  return (
    <NodeViewWrapper as="span" className="ann-link-wrapper">
      <span
        className="ann-embed-chip"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: isHovered ? `${color}22` : `${color}14`,
          border: `1px solid ${isHovered ? color + '77' : color + '44'}`,
          color,
        }}
        title={tooltip}
      >
        {/* Drag grip */}
        <span className="ann-embed-chip__grip" draggable="true" onDragStart={handleDragStart} style={{ opacity: isHovered ? 0.5 : 0 }}>
          <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor">
            <circle cx="1.5" cy="1.5" r="0.9"/><circle cx="4.5" cy="1.5" r="0.9"/>
            <circle cx="1.5" cy="5" r="0.9"/><circle cx="4.5" cy="5" r="0.9"/>
            <circle cx="1.5" cy="8.5" r="0.9"/><circle cx="4.5" cy="8.5" r="0.9"/>
          </svg>
        </span>

        {/* Dot + Label */}
        <span className="ann-embed-chip__main" onClick={handleClick} data-annotation-id={annotationId} data-type="annotation-link">
          <span className="ann-embed-chip__dot" style={{ background: color }} />
          {isEditing ? (
            <input
              className="ann-embed-chip__edit-input"
              value={editLabel}
              onChange={e => setEditLabel(e.target.value)}
              onBlur={handleEditSave}
              onKeyDown={e => e.key === 'Enter' && handleEditSave()}
              autoFocus
              onClick={e => e.stopPropagation()}
              style={{ color }}
            />
          ) : (
            displayLabel
          )}
          {typeLabel && (
            <span className="ann-embed-chip__badge" style={{ background: `${color}20`, color }}>{typeLabel}</span>
          )}
        </span>

        {/* Vorlauftext */}
        {vorlauftext && !isEditing && (
          <span className="ann-embed-chip__vorlauf" style={{ color: `${color}cc` }}>
            {vorlauftext}
          </span>
        )}

        {/* Edit/Delete/Popup-Toggle */}
        {isEditable && isHovered && (
          <>
            <span className="ann-embed-chip__toggle" title="Bearbeiten" onClick={e => { e.stopPropagation(); setEditLabel(label); setIsEditing(!isEditing) }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </span>
            <span
              className="ann-embed-chip__toggle"
              title={showPopup ? 'Popup deaktivieren' : 'Popup aktivieren'}
              onClick={toggleShowPopup}
              style={{ background: showPopup ? `${color}25` : 'rgba(0,0,0,0.05)', color: showPopup ? color : '#999' }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showPopup ? (
                  <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                ) : (
                  <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                )}
              </svg>
            </span>
            <span className="ann-embed-chip__toggle ann-embed-chip__toggle--danger" title="Entfernen" onClick={e => { e.stopPropagation(); handleDelete() }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </span>
          </>
        )}
      </span>
    </NodeViewWrapper>
  )
}
